import { assertNever } from "@msys/common";
import {
  ItemProductSearchFilterPropertyFilterFragment,
  ItemTemplateSearchFilterPropertyFilterFragment,
} from "../templates/templateSearchFilters.generated.js";
import {
  ItemProductSearchFilterBrandFilterFragment,
  ItemProductSearchFilterFavouritesFilterFragment,
  ItemProductSearchFilterPriceFilterFragment,
} from "../products/productSearchFilters.generated.js";
import {
  ArrayFilterOperatorOptions,
  BoolFilterOperatorOptions,
  NumberFilterOperatorOptions,
  TextFilterOperatorOptions,
} from "../products/ProductSearchFilterOperatorOptions.js";
import type { TemplateFiltersFormValues } from "../templates/filters/TemplateFilterFields.js";
import type { Props2NonComputedAllFragment } from "./properties.generated.js";
import type {
  EntitySearchArrayOfFilterOperator,
  EntitySearchFilterOperator,
  EntitySearchBoolFilterOperator,
  EntitySearchBoolInFilterOperator,
  EntitySearchNumberBetweenFilterOperator,
  EntitySearchNumberFilterOperator,
  EntitySearchNumberInFilterOperator,
  EntitySearchTextFilterOperator,
  EntitySearchTextInFilterOperator,
  ItemTemplateSearchFilterPropertyFilterInput,
  TemplateSearchFilterPropertyFilterInput,
} from "../../../clients/graphqlTypes.js";

/** One row of a merged search-filter section: authored or expression-driven. */
export type SearchFilterPropertySlot =
  | ItemTemplateSearchFilterPropertyFilterFragment
  | ItemProductSearchFilterPropertyFilterFragment;

/**
 * Any clause a search-filter row can render — the property-scoped slots plus
 * the product box's named filters, whose clauses carry no `key`.
 */
export type SearchFilterClause =
  | SearchFilterPropertySlot
  | ItemProductSearchFilterPriceFilterFragment
  | ItemProductSearchFilterBrandFilterFragment
  | ItemProductSearchFilterFavouritesFilterFragment;

/**
 * Whether the slot takes its value from an expression. Reads the GraphQL
 * `__typename` rather than inspecting the value, mirroring `isComputedProp`
 * for properties: a valueless authored slot (an expression removed before it
 * ever computed) is authored, not computed.
 */
export function isComputedSearchFilterSlot(slot: SearchFilterPropertySlot) {
  return slot.__typename.endsWith("Computed");
}

/**
 * The operator choices a clause admits. Also the eligibility test: a clause
 * kind reachable here has a `*Computed` counterpart, which is what the `fx`
 * affordance needs — the Id family never appears in a property-filter union.
 */
export function searchFilterOperatorOptions(
  slot: SearchFilterPropertySlot
): { label: string; value: EntitySearchFilterOperator }[] {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchPropertyFilterNumberFilterComputed":
      return NumberFilterOperatorOptions;
    case "EntitySearchPropertyFilterNumberInFilter":
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return NumberFilterOperatorOptions;
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
      return NumberFilterOperatorOptions;
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return ArrayFilterOperatorOptions;
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchPropertyFilterTextFilterComputed":
      return TextFilterOperatorOptions;
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return TextFilterOperatorOptions;
    case "EntitySearchPropertyFilterTextArrayOfFilter":
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return ArrayFilterOperatorOptions;
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchPropertyFilterBoolFilterComputed":
      return BoolFilterOperatorOptions;
    case "EntitySearchPropertyFilterBoolInFilter":
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return BoolFilterOperatorOptions;
    default:
      return assertNever(slot);
  }
}

/** The slot's current operator, whichever flavour it is. */
export function searchFilterOperator(
  slot: SearchFilterPropertySlot
): EntitySearchFilterOperator {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchPropertyFilterNumberFilterComputed":
      return slot.operatorNumber;
    case "EntitySearchPropertyFilterNumberInFilter":
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return slot.operatorNumberIn;
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
      return slot.operatorNumberBetween;
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return slot.operatorNumberArrayOf;
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchPropertyFilterTextFilterComputed":
      return slot.operatorText;
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return slot.operatorTextIn;
    case "EntitySearchPropertyFilterTextArrayOfFilter":
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return slot.operatorTextArrayOf;
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchPropertyFilterBoolFilterComputed":
      return slot.operatorBool;
    case "EntitySearchPropertyFilterBoolInFilter":
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return slot.operatorBoolIn;
    default:
      return assertNever(slot);
  }
}

/** The value family a clause filters on; preserved across an operator change. */
export type SearchFilterDataType = "number" | "text" | "bool";

/** The data type a slot filters on, whichever clause kind it currently is. */
export function searchFilterDataType(
  slot: SearchFilterPropertySlot
): SearchFilterDataType {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchPropertyFilterNumberFilterComputed":
    case "EntitySearchPropertyFilterNumberInFilter":
    case "EntitySearchPropertyFilterNumberInFilterComputed":
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return "number";
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchPropertyFilterTextFilterComputed":
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchPropertyFilterTextInFilterComputed":
    case "EntitySearchPropertyFilterTextArrayOfFilter":
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return "text";
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchPropertyFilterBoolFilterComputed":
    case "EntitySearchPropertyFilterBoolInFilter":
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return "bool";
    default:
      return assertNever(slot);
  }
}

/**
 * The clause kind an operator selects within a data type. The operator options
 * a row offers span several clause kinds — a number filter can be `eq`, `in`,
 * `between` or an array-of — so changing the operator changes the clause, and
 * writing the old clause with the new operator would send `eq` into
 * `operatorNumberIn` and be rejected by the enum.
 */
function clauseStemFor(
  dataType: SearchFilterDataType,
  operator: EntitySearchFilterOperator
): string {
  if (operator === "anyOf" || operator === "allOf") {
    return dataType === "number" ? "NumberArrayOf" : "TextArrayOf";
  }
  if (operator === "between") return "NumberBetween";
  if (operator === "in") {
    return dataType === "number"
      ? "NumberIn"
      : dataType === "text"
        ? "TextIn"
        : "BoolIn";
  }
  return dataType === "number"
    ? "Number"
    : dataType === "text"
      ? "Text"
      : "Bool";
}

/**
 * The write entry that attaches `expr` to this filter, keeping its key and
 * taking the clause kind from the chosen operator. The authored value is
 * dropped: the layer supplies it from now on.
 */
export function searchFilterSlotWithExpression(
  slot: SearchFilterPropertySlot,
  operator: EntitySearchFilterOperator,
  expr: string
): ItemTemplateSearchFilterPropertyFilterInput {
  const key = slot.key;
  switch (clauseStemFor(searchFilterDataType(slot), operator)) {
    case "Number":
      return {
        numberFilterComputed: {
          key,
          operatorNumber: operator as EntitySearchNumberFilterOperator,
          expr,
        },
      };
    case "NumberIn":
      return {
        numberInFilterComputed: {
          key,
          operatorNumberIn: operator as EntitySearchNumberInFilterOperator,
          expr,
        },
      };
    case "NumberBetween":
      return {
        numberBetweenFilterComputed: {
          key,
          operatorNumberBetween:
            operator as EntitySearchNumberBetweenFilterOperator,
          expr,
        },
      };
    case "NumberArrayOf":
      return {
        numberArrayOfFilterComputed: {
          key,
          operatorNumberArrayOf: operator as EntitySearchArrayOfFilterOperator,
          expr,
        },
      };
    case "Text":
      return {
        textFilterComputed: {
          key,
          operatorText: operator as EntitySearchTextFilterOperator,
          expr,
        },
      };
    case "TextIn":
      return {
        textInFilterComputed: {
          key,
          operatorTextIn: operator as EntitySearchTextInFilterOperator,
          expr,
        },
      };
    case "TextArrayOf":
      return {
        textArrayOfFilterComputed: {
          key,
          operatorTextArrayOf: operator as EntitySearchArrayOfFilterOperator,
          expr,
        },
      };
    case "BoolIn":
      return {
        boolInFilterComputed: {
          key,
          operatorBoolIn: operator as EntitySearchBoolInFilterOperator,
          expr,
        },
      };
    default:
      return {
        boolFilterComputed: {
          key,
          operatorBool: operator as EntitySearchBoolFilterOperator,
          expr,
        },
      };
  }
}

/**
 * The write entry that detaches the expression, keeping the filter and the
 * value the expression last produced — `null` where it produced none, which
 * leaves the filter present but contributing no search param. Mirrors
 * `convertToNonComputedInput` for properties.
 */
export function searchFilterSlotWithoutExpression(
  slot: SearchFilterPropertySlot
): ItemTemplateSearchFilterPropertyFilterInput {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilterComputed":
      return {
        numberFilter: {
          key: slot.key,
          operatorNumber: slot.operatorNumber,
          valueNumber: slot.missingValue ? null : slot.valueNumberComputed,
        },
      };
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return {
        numberInFilter: {
          key: slot.key,
          operatorNumberIn: slot.operatorNumberIn,
          valueNumberIn: slot.missingValue ? null : slot.valueNumberInComputed,
        },
      };
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
      return {
        numberBetweenFilter: {
          key: slot.key,
          operatorNumberBetween: slot.operatorNumberBetween,
          valueNumberBetween: slot.missingValue
            ? null
            : slot.valueNumberBetweenComputed,
        },
      };
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return {
        numberArrayOfFilter: {
          key: slot.key,
          operatorNumberArrayOf: slot.operatorNumberArrayOf,
          valueNumberArrayOf: slot.missingValue
            ? null
            : slot.valueNumberArrayOfComputed,
        },
      };
    case "EntitySearchPropertyFilterTextFilterComputed":
      return {
        textFilter: {
          key: slot.key,
          operatorText: slot.operatorText,
          valueText: slot.missingValue ? null : slot.valueTextComputed,
        },
      };
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return {
        textInFilter: {
          key: slot.key,
          operatorTextIn: slot.operatorTextIn,
          valueTextIn: slot.missingValue ? null : slot.valueTextInComputed,
        },
      };
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return {
        textArrayOfFilter: {
          key: slot.key,
          operatorTextArrayOf: slot.operatorTextArrayOf,
          valueTextArrayOf: slot.missingValue
            ? null
            : slot.valueTextArrayOfComputed,
        },
      };
    case "EntitySearchPropertyFilterBoolFilterComputed":
      return {
        boolFilter: {
          key: slot.key,
          operatorBool: slot.operatorBool,
          valueBool: slot.missingValue ? null : slot.valueBoolComputed,
        },
      };
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return {
        boolInFilter: {
          key: slot.key,
          operatorBoolIn: slot.operatorBoolIn,
          valueBoolIn: slot.missingValue ? null : slot.valueBoolInComputed,
        },
      };
    case "EntitySearchPropertyFilterNumberFilter":
      return {
        numberFilter: {
          key: slot.key,
          operatorNumber: slot.operatorNumber,
          valueNumber: slot.valueNumber,
        },
      };
    case "EntitySearchPropertyFilterNumberInFilter":
      return {
        numberInFilter: {
          key: slot.key,
          operatorNumberIn: slot.operatorNumberIn,
          valueNumberIn: slot.valueNumberIn,
        },
      };
    case "EntitySearchPropertyFilterNumberBetweenFilter":
      return {
        numberBetweenFilter: {
          key: slot.key,
          operatorNumberBetween: slot.operatorNumberBetween,
          valueNumberBetween: slot.valueNumberBetween,
        },
      };
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
      return {
        numberArrayOfFilter: {
          key: slot.key,
          operatorNumberArrayOf: slot.operatorNumberArrayOf,
          valueNumberArrayOf: slot.valueNumberArrayOf,
        },
      };
    case "EntitySearchPropertyFilterTextFilter":
      return {
        textFilter: {
          key: slot.key,
          operatorText: slot.operatorText,
          valueText: slot.valueText,
        },
      };
    case "EntitySearchPropertyFilterTextInFilter":
      return {
        textInFilter: {
          key: slot.key,
          operatorTextIn: slot.operatorTextIn,
          valueTextIn: slot.valueTextIn,
        },
      };
    case "EntitySearchPropertyFilterTextArrayOfFilter":
      return {
        textArrayOfFilter: {
          key: slot.key,
          operatorTextArrayOf: slot.operatorTextArrayOf,
          valueTextArrayOf: slot.valueTextArrayOf,
        },
      };
    case "EntitySearchPropertyFilterBoolFilter":
      return {
        boolFilter: {
          key: slot.key,
          operatorBool: slot.operatorBool,
          valueBool: slot.valueBool,
        },
      };
    case "EntitySearchPropertyFilterBoolInFilter":
      return {
        boolInFilter: {
          key: slot.key,
          operatorBoolIn: slot.operatorBoolIn,
          valueBoolIn: slot.valueBoolIn,
        },
      };
    default:
      return assertNever(slot);
  }
}

/**
 * The row value a search filter reads as: its operator label and value, with
 * the not-set placeholder wherever the value is absent — an expression that
 * produced nothing, or an authored filter emptied by removing its expression.
 * Covers both the property-scoped clauses and the product box's bare ones,
 * whose only difference is carrying no `key`.
 */
export function getValueWithOperator(
  filter: SearchFilterClause | undefined | null,
  unit?: string | null | undefined
): string {
  const placeholder = "-";
  if (!filter) return placeholder;
  const withUnit = (text: string) => text + (unit ? ` ${unit}` : "");

  switch (filter.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchNumberFilter": {
      if (filter.valueNumber === null || filter.valueNumber === undefined) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumber
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueNumber}`);
    }
    case "EntitySearchPropertyFilterNumberFilterComputed":
    case "EntitySearchNumberFilterComputed": {
      if (
        filter.valueNumberComputed === null ||
        filter.valueNumberComputed === undefined
      ) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumber
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueNumberComputed}`);
    }
    case "EntitySearchPropertyFilterNumberInFilter": {
      if (
        filter.valueNumberIn === null ||
        filter.valueNumberIn === undefined ||
        filter.valueNumberIn.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberIn
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueNumberIn.join(", ")}`);
    }
    case "EntitySearchPropertyFilterNumberInFilterComputed": {
      if (
        filter.valueNumberInComputed === null ||
        filter.valueNumberInComputed === undefined ||
        filter.valueNumberInComputed.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberIn
      )?.label;
      return withUnit(
        `${operatorLabel} ${filter.valueNumberInComputed.join(", ")}`
      );
    }
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchNumberBetweenFilter": {
      if (
        filter.valueNumberBetween === null ||
        filter.valueNumberBetween === undefined
      ) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberBetween
      )?.label;
      return withUnit(
        `${operatorLabel} ${filter.valueNumberBetween.min}-${filter.valueNumberBetween.max}`
      );
    }
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
    case "EntitySearchNumberBetweenFilterComputed": {
      if (
        filter.valueNumberBetweenComputed === null ||
        filter.valueNumberBetweenComputed === undefined
      ) {
        return placeholder;
      }
      const operatorLabel = NumberFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberBetween
      )?.label;
      return withUnit(
        `${operatorLabel} ${filter.valueNumberBetweenComputed.min}-${filter.valueNumberBetweenComputed.max}`
      );
    }
    case "EntitySearchPropertyFilterNumberArrayOfFilter": {
      if (
        filter.valueNumberArrayOf === null ||
        filter.valueNumberArrayOf === undefined ||
        filter.valueNumberArrayOf.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = ArrayFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberArrayOf
      )?.label;
      return `${operatorLabel} ${filter.valueNumberArrayOf.join(", ")}`;
    }
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed": {
      if (
        filter.valueNumberArrayOfComputed === null ||
        filter.valueNumberArrayOfComputed === undefined ||
        filter.valueNumberArrayOfComputed.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = ArrayFilterOperatorOptions.find(
        opt => opt.value === filter.operatorNumberArrayOf
      )?.label;
      return `${operatorLabel} ${filter.valueNumberArrayOfComputed.join(", ")}`;
    }
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchTextFilter": {
      if (filter.valueText === null || filter.valueText === undefined) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorText
      )?.label;
      return withUnit(`${operatorLabel} "${filter.valueText}"`);
    }
    case "EntitySearchPropertyFilterTextFilterComputed":
    case "EntitySearchTextFilterComputed": {
      if (
        filter.valueTextComputed === null ||
        filter.valueTextComputed === undefined
      ) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorText
      )?.label;
      return withUnit(`${operatorLabel} "${filter.valueTextComputed}"`);
    }
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchTextInFilter": {
      if (
        filter.valueTextIn === null ||
        filter.valueTextIn === undefined ||
        filter.valueTextIn.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorTextIn
      )?.label;
      return `${operatorLabel} ${filter.valueTextIn.join(", ")}`;
    }
    case "EntitySearchPropertyFilterTextInFilterComputed":
    case "EntitySearchTextInFilterComputed": {
      if (
        filter.valueTextInComputed === null ||
        filter.valueTextInComputed === undefined ||
        filter.valueTextInComputed.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorTextIn
      )?.label;
      return `${operatorLabel} ${filter.valueTextInComputed.join(", ")}`;
    }
    case "EntitySearchPropertyFilterTextArrayOfFilter": {
      if (
        filter.valueTextArrayOf === null ||
        filter.valueTextArrayOf === undefined ||
        filter.valueTextArrayOf.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = ArrayFilterOperatorOptions.find(
        opt => opt.value === filter.operatorTextArrayOf
      )?.label;
      return `${operatorLabel} ${filter.valueTextArrayOf.join(", ")}`;
    }
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed": {
      if (
        filter.valueTextArrayOfComputed === null ||
        filter.valueTextArrayOfComputed === undefined ||
        filter.valueTextArrayOfComputed.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = ArrayFilterOperatorOptions.find(
        opt => opt.value === filter.operatorTextArrayOf
      )?.label;
      return `${operatorLabel} ${filter.valueTextArrayOfComputed.join(", ")}`;
    }
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchBoolFilter": {
      if (filter.valueBool === null || filter.valueBool === undefined) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorBool
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueBool}`);
    }
    case "EntitySearchPropertyFilterBoolFilterComputed":
    case "EntitySearchBoolFilterComputed": {
      if (
        filter.valueBoolComputed === null ||
        filter.valueBoolComputed === undefined
      ) {
        return placeholder;
      }
      const operatorLabel = TextFilterOperatorOptions.find(
        opt => opt.value === filter.operatorBool
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueBoolComputed}`);
    }
    case "EntitySearchPropertyFilterBoolInFilter": {
      if (
        filter.valueBoolIn === null ||
        filter.valueBoolIn === undefined ||
        filter.valueBoolIn.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = BoolFilterOperatorOptions.find(
        opt => opt.value === filter.operatorBoolIn
      )?.label;
      return withUnit(`${operatorLabel} ${filter.valueBoolIn.join(", ")}`);
    }
    case "EntitySearchPropertyFilterBoolInFilterComputed": {
      if (
        filter.valueBoolInComputed === null ||
        filter.valueBoolInComputed === undefined ||
        filter.valueBoolInComputed.length === 0
      ) {
        return placeholder;
      }
      const operatorLabel = BoolFilterOperatorOptions.find(
        opt => opt.value === filter.operatorBoolIn
      )?.label;
      return withUnit(
        `${operatorLabel} ${filter.valueBoolInComputed.join(", ")}`
      );
    }
    default:
      return assertNever(filter);
  }
}

/**
 * Round-trip a stored slot back to its write entry, so a write that edits one
 * filter can re-emit every other row untouched — the merged mutation replaces
 * the whole array.
 */
export function searchFilterSlotToInput(
  slot: SearchFilterPropertySlot
): ItemTemplateSearchFilterPropertyFilterInput {
  return isComputedSearchFilterSlot(slot) && "expr" in slot
    ? searchFilterSlotWithExpression(
        slot,
        searchFilterOperator(slot),
        slot.expr
      )
    : searchFilterSlotWithoutExpression(slot);
}

/**
 * The filter key a write entry addresses. Every clause input carries it, and
 * exactly one clause is populated, so the entry's own key is whichever clause
 * it holds — needed to reconcile a generated entry list against stored slots.
 */
export function searchFilterInputKey(
  entry: ItemTemplateSearchFilterPropertyFilterInput
): string | undefined {
  for (const clause of Object.values(entry)) {
    if (clause && "key" in clause) return clause.key;
  }
  return undefined;
}

/**
 * Whether an authored slot carries a value. A slot left valueless by removing
 * an expression that never computed is authored but has nothing to show — and
 * cannot be represented in the authored filter-set form, whose clause inputs
 * require a value.
 */
export function searchFilterSlotHasValue(
  slot: SearchFilterPropertySlot
): boolean {
  return getValueWithOperator(slot) !== "-";
}

/**
 * Seed the authored filter-set form. Only the authored rows that carry a
 * value are representable — the form's clause inputs require one — so
 * expression-driven rows and valueless authored ones are left out and
 * preserved around the form by the mutation hook.
 */
export function searchFilterPropertyFiltersToFormValues(
  propertyFilters: ItemTemplateSearchFilterPropertyFilterFragment[]
): Partial<TemplateFiltersFormValues> {
  return {
    properties:
      propertyFilters.flatMap<TemplateSearchFilterPropertyFilterInput>(
        propertyFilter => {
          switch (propertyFilter.__typename) {
            case "EntitySearchPropertyFilterBoolFilter": {
              const value = propertyFilter.valueBool;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  boolFilter: {
                    operatorBool: propertyFilter.operatorBool,
                    valueBool: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterBoolInFilter": {
              const value = propertyFilter.valueBoolIn;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  boolInFilter: {
                    operatorBoolIn: propertyFilter.operatorBoolIn,
                    valueBoolIn: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterNumberFilter": {
              const value = propertyFilter.valueNumber;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  numberFilter: {
                    operatorNumber: propertyFilter.operatorNumber,
                    valueNumber: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterNumberInFilter": {
              const value = propertyFilter.valueNumberIn;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  numberInFilter: {
                    operatorNumberIn: propertyFilter.operatorNumberIn,
                    valueNumberIn: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterNumberBetweenFilter": {
              const value = propertyFilter.valueNumberBetween;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  numberBetweenFilter: {
                    operatorNumberBetween: propertyFilter.operatorNumberBetween,
                    valueNumberBetween: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterNumberArrayOfFilter": {
              const value = propertyFilter.valueNumberArrayOf;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  numberArrayOfFilter: {
                    operatorNumberArrayOf: propertyFilter.operatorNumberArrayOf,
                    valueNumberArrayOf: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterTextFilter": {
              const value = propertyFilter.valueText;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  textFilter: {
                    operatorText: propertyFilter.operatorText,
                    valueText: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterTextInFilter": {
              const value = propertyFilter.valueTextIn;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  textInFilter: {
                    operatorTextIn: propertyFilter.operatorTextIn,
                    valueTextIn: value,
                  },
                },
              ];
            }
            case "EntitySearchPropertyFilterTextArrayOfFilter": {
              const value = propertyFilter.valueTextArrayOf;
              if (value === null || value === undefined) return [];
              return [
                {
                  key: propertyFilter.key,
                  textArrayOfFilter: {
                    operatorTextArrayOf: propertyFilter.operatorTextArrayOf,
                    valueTextArrayOf: value,
                  },
                },
              ];
            }
            default:
              // Expression-driven rows are not authored in this form.
              return [];
          }
        }
      ),
  };
}

/**
 * The presentational props a property-filter slot contributes to
 * {@link SearchFilterField}, so each site wires only its persistence.
 */
export function searchFilterFieldPropsForSlot(
  slot: SearchFilterPropertySlot,
  unit?: string | null
) {
  return {
    value: getValueWithOperator(slot, unit),
    isComputed: isComputedSearchFilterSlot(slot),
    expression: "expr" in slot ? slot.expr : "",
    operator: searchFilterOperator(slot),
    operatorOptions: searchFilterOperatorOptions(slot),
  };
}

/**
 * Convert a slot between flavours in *fragment* shape rather than write shape,
 * for the recommendation modal, whose rows are buffered in Formik and submitted
 * as one payload rather than persisted per edit. Like the write-shape twin, the
 * clause kind follows the chosen operator.
 */
export function withExpressionFragment(
  slot: ItemTemplateSearchFilterPropertyFilterFragment,
  operator: EntitySearchFilterOperator,
  expr: string
): ItemTemplateSearchFilterPropertyFilterFragment {
  const key = slot.key;
  const base = { key, expr, missingValue: true } as const;
  switch (clauseStemFor(searchFilterDataType(slot), operator)) {
    case "Number":
      return {
        __typename: "EntitySearchPropertyFilterNumberFilterComputed",
        ...base,
        operatorNumber: operator as EntitySearchNumberFilterOperator,
        valueNumberComputed: null,
      };
    case "NumberIn":
      return {
        __typename: "EntitySearchPropertyFilterNumberInFilterComputed",
        ...base,
        operatorNumberIn: operator as EntitySearchNumberInFilterOperator,
        valueNumberInComputed: null,
      };
    case "NumberBetween":
      return {
        __typename: "EntitySearchPropertyFilterNumberBetweenFilterComputed",
        ...base,
        operatorNumberBetween:
          operator as EntitySearchNumberBetweenFilterOperator,
        valueNumberBetweenComputed: null,
      };
    case "NumberArrayOf":
      return {
        __typename: "EntitySearchPropertyFilterNumberArrayOfFilterComputed",
        ...base,
        operatorNumberArrayOf: operator as EntitySearchArrayOfFilterOperator,
        valueNumberArrayOfComputed: null,
      };
    case "Text":
      return {
        __typename: "EntitySearchPropertyFilterTextFilterComputed",
        ...base,
        operatorText: operator as EntitySearchTextFilterOperator,
        valueTextComputed: null,
      };
    case "TextIn":
      return {
        __typename: "EntitySearchPropertyFilterTextInFilterComputed",
        ...base,
        operatorTextIn: operator as EntitySearchTextInFilterOperator,
        valueTextInComputed: null,
      };
    case "TextArrayOf":
      return {
        __typename: "EntitySearchPropertyFilterTextArrayOfFilterComputed",
        ...base,
        operatorTextArrayOf: operator as EntitySearchArrayOfFilterOperator,
        valueTextArrayOfComputed: null,
      };
    case "BoolIn":
      return {
        __typename: "EntitySearchPropertyFilterBoolInFilterComputed",
        ...base,
        operatorBoolIn: operator as EntitySearchBoolInFilterOperator,
        valueBoolInComputed: null,
      };
    default:
      return {
        __typename: "EntitySearchPropertyFilterBoolFilterComputed",
        ...base,
        operatorBool: operator as EntitySearchBoolFilterOperator,
        valueBoolComputed: null,
      };
  }
}

/** The fragment-shaped twin of `searchFilterSlotWithoutExpression`. */
export function withoutExpressionFragment(
  slot: ItemTemplateSearchFilterPropertyFilterFragment
): ItemTemplateSearchFilterPropertyFilterFragment {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterNumberFilter",
        key: slot.key,
        operatorNumber: slot.operatorNumber,
        valueNumber: slot.missingValue ? null : slot.valueNumberComputed,
      };
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterNumberInFilter",
        key: slot.key,
        operatorNumberIn: slot.operatorNumberIn,
        valueNumberIn: slot.missingValue ? null : slot.valueNumberInComputed,
      };
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterNumberBetweenFilter",
        key: slot.key,
        operatorNumberBetween: slot.operatorNumberBetween,
        valueNumberBetween: slot.missingValue
          ? null
          : slot.valueNumberBetweenComputed,
      };
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterNumberArrayOfFilter",
        key: slot.key,
        operatorNumberArrayOf: slot.operatorNumberArrayOf,
        valueNumberArrayOf: slot.missingValue
          ? null
          : slot.valueNumberArrayOfComputed,
      };
    case "EntitySearchPropertyFilterTextFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterTextFilter",
        key: slot.key,
        operatorText: slot.operatorText,
        valueText: slot.missingValue ? null : slot.valueTextComputed,
      };
    case "EntitySearchPropertyFilterTextInFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterTextInFilter",
        key: slot.key,
        operatorTextIn: slot.operatorTextIn,
        valueTextIn: slot.missingValue ? null : slot.valueTextInComputed,
      };
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterTextArrayOfFilter",
        key: slot.key,
        operatorTextArrayOf: slot.operatorTextArrayOf,
        valueTextArrayOf: slot.missingValue
          ? null
          : slot.valueTextArrayOfComputed,
      };
    case "EntitySearchPropertyFilterBoolFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterBoolFilter",
        key: slot.key,
        operatorBool: slot.operatorBool,
        valueBool: slot.missingValue ? null : slot.valueBoolComputed,
      };
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return {
        __typename: "EntitySearchPropertyFilterBoolInFilter",
        key: slot.key,
        operatorBoolIn: slot.operatorBoolIn,
        valueBoolIn: slot.missingValue ? null : slot.valueBoolInComputed,
      };
    default:
      return slot;
  }
}

/** A fresh authored filter for a template-type property, valueless until set. */
export function newAuthoredFilterForProp(
  prop: Props2NonComputedAllFragment
): ItemTemplateSearchFilterPropertyFilterFragment {
  switch (prop.__typename) {
    case "Props2Bool":
      return {
        __typename: "EntitySearchPropertyFilterBoolFilter",
        key: prop.key,
        operatorBool: "eq",
        valueBool: null,
      };
    case "Props2Number":
      return {
        __typename: "EntitySearchPropertyFilterNumberFilter",
        key: prop.key,
        operatorNumber: "eq",
        valueNumber: null,
      };
    case "Props2NumberArray":
      return {
        __typename: "EntitySearchPropertyFilterNumberArrayOfFilter",
        key: prop.key,
        operatorNumberArrayOf: "anyOf",
        valueNumberArrayOf: null,
      };
    case "Props2Text":
      return {
        __typename: "EntitySearchPropertyFilterTextFilter",
        key: prop.key,
        operatorText: "eq",
        valueText: null,
      };
    case "Props2TextArray":
      return {
        __typename: "EntitySearchPropertyFilterTextArrayOfFilter",
        key: prop.key,
        operatorTextArrayOf: "anyOf",
        valueTextArrayOf: null,
      };
    default:
      return assertNever(prop);
  }
}

/**
 * The authored values a slot currently carries, flattened to a list so a row's
 * value control can render single- and multi-valued clauses alike. A computed
 * slot contributes nothing: its value belongs to the layer.
 */
export function searchFilterAuthoredValues(
  slot: SearchFilterPropertySlot
): Array<string | number | boolean> {
  switch (slot.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
      return slot.valueNumber !== null && slot.valueNumber !== undefined
        ? [slot.valueNumber]
        : [];
    case "EntitySearchPropertyFilterNumberInFilter":
      return slot.valueNumberIn ?? [];
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
      return slot.valueNumberArrayOf ?? [];
    case "EntitySearchPropertyFilterNumberBetweenFilter":
      return slot.valueNumberBetween
        ? [slot.valueNumberBetween.min, slot.valueNumberBetween.max]
        : [];
    case "EntitySearchPropertyFilterTextFilter":
      return slot.valueText !== null && slot.valueText !== undefined
        ? [slot.valueText]
        : [];
    case "EntitySearchPropertyFilterTextInFilter":
      return slot.valueTextIn ?? [];
    case "EntitySearchPropertyFilterTextArrayOfFilter":
      return slot.valueTextArrayOf ?? [];
    case "EntitySearchPropertyFilterBoolFilter":
      return slot.valueBool !== null && slot.valueBool !== undefined
        ? [slot.valueBool]
        : [];
    case "EntitySearchPropertyFilterBoolInFilter":
      return slot.valueBoolIn ?? [];
    default:
      return [];
  }
}

/**
 * The values a clause holds, whatever its flavour — authored or computed,
 * property-keyed or one of the product context's named filters. Unlike
 * {@link searchFilterAuthoredValues}, which serves the write path and so
 * ignores computed clauses, this reads whatever the clause currently shows.
 */
export function searchFilterClauseValues(
  filter: SearchFilterClause | null | undefined
): Array<string | number | boolean> {
  if (!filter) return [];
  const one = <T>(value: T | null | undefined): T[] =>
    value !== null && value !== undefined ? [value] : [];

  switch (filter.__typename) {
    case "EntitySearchPropertyFilterNumberFilter":
    case "EntitySearchNumberFilter":
      return one(filter.valueNumber);
    case "EntitySearchPropertyFilterNumberFilterComputed":
    case "EntitySearchNumberFilterComputed":
      return one(filter.valueNumberComputed);
    case "EntitySearchPropertyFilterNumberInFilter":
      return filter.valueNumberIn ?? [];
    case "EntitySearchPropertyFilterNumberInFilterComputed":
      return filter.valueNumberInComputed ?? [];
    case "EntitySearchPropertyFilterNumberArrayOfFilter":
      return filter.valueNumberArrayOf ?? [];
    case "EntitySearchPropertyFilterNumberArrayOfFilterComputed":
      return filter.valueNumberArrayOfComputed ?? [];
    case "EntitySearchPropertyFilterNumberBetweenFilter":
    case "EntitySearchNumberBetweenFilter":
      return filter.valueNumberBetween
        ? [filter.valueNumberBetween.min, filter.valueNumberBetween.max]
        : [];
    case "EntitySearchPropertyFilterNumberBetweenFilterComputed":
    case "EntitySearchNumberBetweenFilterComputed":
      return filter.valueNumberBetweenComputed
        ? [
            filter.valueNumberBetweenComputed.min,
            filter.valueNumberBetweenComputed.max,
          ]
        : [];
    case "EntitySearchPropertyFilterTextFilter":
    case "EntitySearchTextFilter":
      return one(filter.valueText);
    case "EntitySearchPropertyFilterTextFilterComputed":
    case "EntitySearchTextFilterComputed":
      return one(filter.valueTextComputed);
    case "EntitySearchPropertyFilterTextInFilter":
    case "EntitySearchTextInFilter":
      return filter.valueTextIn ?? [];
    case "EntitySearchPropertyFilterTextInFilterComputed":
    case "EntitySearchTextInFilterComputed":
      return filter.valueTextInComputed ?? [];
    case "EntitySearchPropertyFilterTextArrayOfFilter":
      return filter.valueTextArrayOf ?? [];
    case "EntitySearchPropertyFilterTextArrayOfFilterComputed":
      return filter.valueTextArrayOfComputed ?? [];
    case "EntitySearchPropertyFilterBoolFilter":
    case "EntitySearchBoolFilter":
      return one(filter.valueBool);
    case "EntitySearchPropertyFilterBoolFilterComputed":
    case "EntitySearchBoolFilterComputed":
      return one(filter.valueBoolComputed);
    case "EntitySearchPropertyFilterBoolInFilter":
      return filter.valueBoolIn ?? [];
    case "EntitySearchPropertyFilterBoolInFilterComputed":
      return filter.valueBoolInComputed ?? [];
    default:
      return [];
  }
}

/**
 * A clause's value on its own, for a list that shows the operator in its own
 * column. `-` when the filter holds no value — an expression that has not
 * computed, or a filter created but not yet given one.
 */
export function searchFilterValueText(
  filter: SearchFilterClause | null | undefined,
  opts?: { unit?: string | null; boolLabels?: { yes: string; no: string } }
): string {
  const values = searchFilterClauseValues(filter);
  if (values.length === 0) return "-";
  const text = values
    .map(value =>
      typeof value === "boolean"
        ? value
          ? (opts?.boolLabels?.yes ?? "true")
          : (opts?.boolLabels?.no ?? "false")
        : String(value)
    )
    .join(", ");
  return opts?.unit ? `${text} ${opts.unit}` : text;
}

/** The row's value options taken from a template type's property definition. */
export function searchFilterValueOptionsFromProps2(
  prop: Props2NonComputedAllFragment | undefined
): SearchFilterValueOptionsShape {
  switch (prop?.__typename) {
    case "Props2Bool":
      return { kind: "bool" };
    case "Props2Number":
    case "Props2NumberArray":
      return {
        kind: prop.__typename === "Props2Number" ? "number" : "numberArray",
        unit: prop.unit,
        options: (prop.allowedValuesNumber ?? []).map(v => ({
          value: String(v.allowedNumber),
          label: String(v.allowedNumber),
        })),
      };
    case "Props2Text":
    case "Props2TextArray":
      return {
        kind: prop.__typename === "Props2Text" ? "text" : "textArray",
        options: (prop.allowedValuesText ?? []).map(v => ({
          value: v.allowedText,
          label: v.allowedText,
        })),
      };
    default:
      // No definition to read: fall back to a free text value.
      return { kind: "text", options: [] };
  }
}

/** Structural echo of `SearchFilterValueOptions`, kept here to avoid a cycle. */
export type SearchFilterValueOptionsShape =
  | { kind: "bool" }
  | {
      kind: "number" | "numberArray";
      options: Array<{ value: string; label: string }>;
      unit?: string | null;
    }
  | {
      kind: "text" | "textArray";
      options: Array<{ value: string; label: string }>;
    };

/** Re-express an authored slot under a different operator. */
export function searchFilterSlotWithOperator(
  slot: SearchFilterPropertySlot,
  operator: EntitySearchFilterOperator
): ItemTemplateSearchFilterPropertyFilterInput {
  return searchFilterEntry(
    slot.key,
    searchFilterDataType(slot),
    operator,
    searchFilterAuthoredValues(slot)
  );
}

/**
 * Build the authored clause a filter key takes under a given operator, holding
 * the supplied values as far as that clause can: a single-valued operator keeps
 * the first, `between` reads the first two, a list operator keeps them all.
 *
 * The operator options a row offers span several clause kinds, so both changing
 * the operator and editing the value go through here — the authored twin of
 * {@link searchFilterSlotWithExpression}.
 */
export function searchFilterEntry(
  key: string,
  dataType: SearchFilterDataType,
  operator: EntitySearchFilterOperator,
  values: ReadonlyArray<string | number | boolean>
): ItemTemplateSearchFilterPropertyFilterInput {
  const numbers = values.map(Number).filter(v => !Number.isNaN(v));
  const texts = values.map(String);
  const first = <T>(list: T[]): T | null => (list.length > 0 ? list[0] : null);

  switch (clauseStemFor(dataType, operator)) {
    case "Number":
      return {
        numberFilter: {
          key,
          operatorNumber: operator as EntitySearchNumberFilterOperator,
          valueNumber: first(numbers),
        },
      };
    case "NumberIn":
      return {
        numberInFilter: {
          key,
          operatorNumberIn: operator as EntitySearchNumberInFilterOperator,
          valueNumberIn: numbers.length > 0 ? numbers : null,
        },
      };
    case "NumberBetween":
      return {
        numberBetweenFilter: {
          key,
          operatorNumberBetween:
            operator as EntitySearchNumberBetweenFilterOperator,
          valueNumberBetween:
            numbers.length >= 2
              ? { min: numbers[0], max: numbers[1] }
              : numbers.length === 1
                ? { min: numbers[0], max: numbers[0] }
                : null,
        },
      };
    case "NumberArrayOf":
      return {
        numberArrayOfFilter: {
          key,
          operatorNumberArrayOf: operator as EntitySearchArrayOfFilterOperator,
          valueNumberArrayOf: numbers.length > 0 ? numbers : null,
        },
      };
    case "Text":
      return {
        textFilter: {
          key,
          operatorText: operator as EntitySearchTextFilterOperator,
          valueText: first(texts),
        },
      };
    case "TextIn":
      return {
        textInFilter: {
          key,
          operatorTextIn: operator as EntitySearchTextInFilterOperator,
          valueTextIn: texts.length > 0 ? texts : null,
        },
      };
    case "TextArrayOf":
      return {
        textArrayOfFilter: {
          key,
          operatorTextArrayOf: operator as EntitySearchArrayOfFilterOperator,
          valueTextArrayOf: texts.length > 0 ? texts : null,
        },
      };
    case "BoolIn":
      return {
        boolInFilter: {
          key,
          operatorBoolIn: operator as EntitySearchBoolInFilterOperator,
          valueBoolIn:
            values.length > 0
              ? values.map(v => v === true || v === "true")
              : null,
        },
      };
    default:
      return {
        boolFilter: {
          key,
          operatorBool: operator as EntitySearchBoolFilterOperator,
          valueBool: values.length > 0 ? values[0] === true : null,
        },
      };
  }
}

/**
 * Builds a row's edit from the values it now holds. The two editing sites want
 * different shapes for the same edit — the boxes persist a write entry, the
 * recommendation modal buffers a fragment — so the value control takes the
 * builder from its caller rather than committing to one of them.
 */
export type SearchFilterEntryBuilder<TEntry> = (
  key: string,
  dataType: SearchFilterDataType,
  operator: EntitySearchFilterOperator,
  values: ReadonlyArray<string | number | boolean>
) => TEntry;

/**
 * The fragment-shaped twin of {@link searchFilterEntry}, for the recommendation
 * modal's Formik-buffered rows. Same clause choice, same value-keeping rules.
 */
export function authoredFilterFragment(
  key: string,
  dataType: SearchFilterDataType,
  operator: EntitySearchFilterOperator,
  values: ReadonlyArray<string | number | boolean>
): ItemTemplateSearchFilterPropertyFilterFragment {
  const numbers = values.map(Number).filter(v => !Number.isNaN(v));
  const texts = values.map(String);

  switch (clauseStemFor(dataType, operator)) {
    case "Number":
      return {
        __typename: "EntitySearchPropertyFilterNumberFilter",
        key,
        operatorNumber: operator as EntitySearchNumberFilterOperator,
        valueNumber: numbers.length > 0 ? numbers[0] : null,
      };
    case "NumberIn":
      return {
        __typename: "EntitySearchPropertyFilterNumberInFilter",
        key,
        operatorNumberIn: operator as EntitySearchNumberInFilterOperator,
        valueNumberIn: numbers.length > 0 ? numbers : null,
      };
    case "NumberBetween":
      return {
        __typename: "EntitySearchPropertyFilterNumberBetweenFilter",
        key,
        operatorNumberBetween:
          operator as EntitySearchNumberBetweenFilterOperator,
        valueNumberBetween:
          numbers.length >= 2
            ? {
                __typename: "ValueNumberBetween",
                min: numbers[0],
                max: numbers[1],
              }
            : numbers.length === 1
              ? {
                  __typename: "ValueNumberBetween",
                  min: numbers[0],
                  max: numbers[0],
                }
              : null,
      };
    case "NumberArrayOf":
      return {
        __typename: "EntitySearchPropertyFilterNumberArrayOfFilter",
        key,
        operatorNumberArrayOf: operator as EntitySearchArrayOfFilterOperator,
        valueNumberArrayOf: numbers.length > 0 ? numbers : null,
      };
    case "Text":
      return {
        __typename: "EntitySearchPropertyFilterTextFilter",
        key,
        operatorText: operator as EntitySearchTextFilterOperator,
        valueText: texts.length > 0 ? texts[0] : null,
      };
    case "TextIn":
      return {
        __typename: "EntitySearchPropertyFilterTextInFilter",
        key,
        operatorTextIn: operator as EntitySearchTextInFilterOperator,
        valueTextIn: texts.length > 0 ? texts : null,
      };
    case "TextArrayOf":
      return {
        __typename: "EntitySearchPropertyFilterTextArrayOfFilter",
        key,
        operatorTextArrayOf: operator as EntitySearchArrayOfFilterOperator,
        valueTextArrayOf: texts.length > 0 ? texts : null,
      };
    case "BoolIn":
      return {
        __typename: "EntitySearchPropertyFilterBoolInFilter",
        key,
        operatorBoolIn: operator as EntitySearchBoolInFilterOperator,
        valueBoolIn:
          values.length > 0
            ? values.map(v => v === true || v === "true")
            : null,
      };
    default:
      return {
        __typename: "EntitySearchPropertyFilterBoolFilter",
        key,
        operatorBool: operator as EntitySearchBoolFilterOperator,
        valueBool: values.length > 0 ? values[0] === true : null,
      };
  }
}

/** The fragment-shaped twin of {@link searchFilterSlotWithOperator}. */
export function withOperatorFragment(
  slot: ItemTemplateSearchFilterPropertyFilterFragment,
  operator: EntitySearchFilterOperator
): ItemTemplateSearchFilterPropertyFilterFragment {
  return authoredFilterFragment(
    slot.key,
    searchFilterDataType(slot),
    operator,
    searchFilterAuthoredValues(slot)
  );
}
